import { useState, useEffect, useMemo } from "react";
import { Search, Bell, ChevronDown, Command, Plus, LogOut } from "lucide-react";
import { Input } from "@project/components/ui/input";
import { SidebarTrigger } from "@project/components/ui/sidebar";
import { Button } from "@project/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@project/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@project/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@project/components/ui/dialog";
import { useAuth } from "@project/contexts/AuthContext";
import {
  mockNotifications,
  mockJobs,
  mockCandidates,
  mockInterviews,
} from "@project/data/mockData";
import hireflowLogo from "@project/assets/hireflow-logo.png";
import { toast } from "@project/hooks/use-toast";

const breadcrumbMap: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/jobs": "Jobs",
  "/candidates": "Candidates",
  "/interviews": "Interviews",
  "/offers": "Offers",
  "/notifications": "Notifications",
  "/support": "Support",
  "/settings": "Settings",
};

export function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }
    const q = searchQuery.toLowerCase();
    const results: {
      type: string;
      title: string;
      subtitle: string;
      url: string;
    }[] = [];
    mockJobs
      .filter(
        j =>
          j.title.toLowerCase().includes(q) || j.jobId.toLowerCase().includes(q)
      )
      .slice(0, 3)
      .forEach(j =>
        results.push({
          type: "Job",
          title: j.title,
          subtitle: `${j.jobId} · ${j.department}`,
          url: `/jobs/${j.id}`,
        })
      );
    mockCandidates
      .filter(
        c =>
          c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
      )
      .slice(0, 3)
      .forEach(c =>
        results.push({
          type: "Candidate",
          title: c.name,
          subtitle: `${c.jobTitle} · ${c.matchScore}% match`,
          url: `/candidates/${c.id}`,
        })
      );
    mockInterviews
      .filter(
        i =>
          i.candidateName.toLowerCase().includes(q) ||
          i.jobTitle.toLowerCase().includes(q)
      )
      .slice(0, 2)
      .forEach(i =>
        results.push({
          type: "Interview",
          title: i.candidateName,
          subtitle: `${i.jobTitle} · ${i.round}`,
          url: `/interviews/${i.id}`,
        })
      );
    return results;
  }, [searchQuery]);

  const getBreadcrumb = () => {
    if (location.pathname === "/dashboard") {
      return "Dashboard";
    }
    const base = `/${pathSegments[0]}`;
    return (
      breadcrumbMap[base] ||
      pathSegments[0]
        ?.replace(/-/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase()) ||
      "Dashboard"
    );
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast({ title: "Logged out" });
  };

  return (
    <>
      <header className="glass-header sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border/50 px-4 sm:px-5">
        <div className="flex items-center gap-3">
          <SidebarTrigger className="hidden h-8 w-8 text-foreground/60 transition-colors hover:text-foreground sm:flex" />
          <div className="flex items-center gap-2 sm:hidden">
            <img
              src={hireflowLogo}
              alt="HireFlow"
              className="h-7 w-auto object-contain"
            />
            <h1 className="font-display text-[15px] font-bold text-foreground">
              {getBreadcrumb()}
            </h1>
          </div>
          <div className="hidden items-center gap-1.5 text-[13px] sm:flex">
            <span className="text-muted-foreground">Home</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="font-display font-medium text-foreground">
              {getBreadcrumb()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="relative hidden h-8 w-48 cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-muted/40 px-2.5 text-[12px] text-muted-foreground transition-all hover:border-border/60 lg:flex"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="inline-flex h-[18px] select-none items-center gap-0.5 rounded border border-border/40 bg-muted/40 px-1 font-mono text-[9px] font-medium">
              <Command className="h-2 w-2" />K
            </kbd>
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground/60 transition-all hover:bg-muted/40 hover:text-foreground lg:hidden"
          >
            <Search className="h-4 w-4" />
          </button>

          <Popover open={notifOpen} onOpenChange={setNotifOpen}>
            <PopoverTrigger asChild>
              <button className="relative flex h-8 w-8 items-center justify-center rounded-lg text-foreground/60 transition-all hover:bg-muted/40 hover:text-foreground">
                <Bell className="h-4 w-4" />
                <span className="absolute right-1.5 top-1.5 h-[6px] w-[6px] rounded-full bg-destructive ring-[1.5px] ring-card" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[calc(100vw-2rem)] p-0 sm:w-80"
              align="end"
            >
              <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
                <h3 className="font-display text-[13px] font-semibold text-foreground">
                  Notifications
                </h3>
              </div>
              <div className="max-h-64 divide-y divide-border/30 overflow-y-auto">
                {mockNotifications.map(n => (
                  <div
                    key={n.id}
                    className={`cursor-pointer px-4 py-2.5 transition-colors hover:bg-accent/15 ${!n.read ? "bg-primary/3" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-medium text-foreground">
                          {n.title}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {n.description}
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {n.time}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <button
            onClick={handleLogout}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-destructive transition-all hover:bg-destructive/10 sm:hidden"
          >
            <LogOut className="h-4 w-4" />
          </button>

          <div className="hidden h-5 w-px bg-border/40 sm:block" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="group hidden cursor-pointer items-center gap-2 rounded-lg py-1 pl-0.5 pr-1 transition-all hover:bg-muted/30 sm:flex">
                <div className="ring-1.5 flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-glow ring-primary/15">
                  <span className="text-[10px] font-bold text-primary-foreground">
                    {user?.avatar || "?"}
                  </span>
                </div>
                <div className="hidden text-left md:block">
                  <p className="font-display text-[12px] font-semibold leading-tight text-foreground">
                    {user?.name || "User"}
                  </p>
                </div>
                <ChevronDown className="hidden h-3 w-3 text-muted-foreground md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className="text-[12px]"
              >
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/support")}
                className="text-[12px]"
              >
                Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-[12px] text-destructive"
              >
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Global Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogTitle className="sr-only">Search</DialogTitle>
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              placeholder="Search jobs, candidates, interviews..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
            />
            <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ESC
            </kbd>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {searchQuery.trim() && searchResults.length === 0 && (
              <div className="p-6 text-center text-[13px] text-muted-foreground">
                No results found
              </div>
            )}
            {searchResults.map((r, i) => (
              <button
                key={i}
                onClick={() => {
                  navigate(r.url);
                  setSearchOpen(false);
                  setSearchQuery("");
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent/40"
              >
                <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                  {r.type}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-semibold text-foreground">
                    {r.title}
                  </p>
                  <p className="truncate text-[10px] text-muted-foreground">
                    {r.subtitle}
                  </p>
                </div>
              </button>
            ))}
            {!searchQuery.trim() && (
              <div className="space-y-2 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Quick Links
                </p>
                {[
                  { label: "Dashboard", url: "/dashboard" },
                  { label: "Jobs", url: "/jobs" },
                  { label: "Candidates", url: "/candidates" },
                  { label: "Interviews", url: "/interviews" },
                  { label: "Settings", url: "/settings" },
                ].map(l => (
                  <button
                    key={l.url}
                    onClick={() => {
                      navigate(l.url);
                      setSearchOpen(false);
                    }}
                    className="w-full rounded-lg px-3 py-2 text-left text-[12px] font-medium text-foreground transition-colors hover:bg-accent/40"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
